import { useCallback, useEffect, useRef, useState } from "react";

import { mergeClassNames } from "../../utils/classes";
import Icon from "../Icon/Icon";
import { SG } from "../SpacingGroup/SpacingGroup";
import { Heading } from "../TypographicElements/TypographicElements";

import type { ReactNode } from "react";

import * as styles from "./toast.scss";

export type ToastType = "success" | "info" | "warning" | "error";

interface ToastProps {
	severity: ToastType;
	title?: string;
	children: ReactNode;
	onClose: () => void;
	testId?: string;
	persist?: boolean;
	/**
	 * Disable animation to display the toasts staticly, this defaults to false and should *NOT* be used in product code.
	 */
	_storybook_withoutAnimation?: boolean;
}

const icons: Record<ToastProps["severity"], ReactNode> = {
	error: <Icon name="attention-alt" size="24" noMargin className={styles.icon} />,
	warning: <Icon name="attention-alt" size="24" noMargin className={styles.icon} />,
	success: <Icon name="check" size="24" noMargin className={styles.icon} />,
	info: <Icon name="info" size="24" noMargin className={styles.icon} />,
};

export const Toast = ({
	severity: type,
	children,
	onClose,
	testId,
	title,
	persist,
	_storybook_withoutAnimation = false,
}: ToastProps) => {
	const timer = useRef<number | undefined>(undefined);
	const [close, setClose] = useState(false);

	const handleClose = useCallback(() => {
		setClose(true);
		window.clearTimeout(timer.current);
		setTimeout(onClose, 700);
	}, [onClose]);

	useEffect(() => {
		if (!persist) {
			timer.current = window.setTimeout(handleClose, 5000);
			return () => window.clearTimeout(timer.current);
		}
		return undefined;
	}, [persist, handleClose]);

	const handleMouseEnter = () => {
		if (timer.current) {
			window.clearTimeout(timer.current);
			timer.current = undefined;
		}
	};

	const handleMouseLeave = () => {
		if (!persist) {
			timer.current = window.setTimeout(handleClose, 5000);
		}
	};

	return (
		<div
			data-testid={testId}
			className={mergeClassNames(
				styles.container,
				styles[type],
				!_storybook_withoutAnimation && (close ? styles.slideOut : styles.slideIn),
			)}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			{icons[type]}
			<div className={styles.contentCenter}>
				<SG section>
					{title && (
						<Heading style="body-bold" color="ui-1">
							{title}
						</Heading>
					)}
					{children && children}
				</SG>
			</div>
			<div>
				<button className={styles.closeButton} type="button" onClick={handleClose}>
					<Icon name="cancel" size="24" className={styles.closeButton} />
				</button>
			</div>
		</div>
	);
};
